/* eslint-disable @typescript-eslint/no-explicit-any */
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'src/hooks';
import { creatorProfilePageRoute, mymeDetailsPageRoute } from 'src/routes/route-list';
import { ImageCell, LinkCellWrapper, PriceCell, TooltipCell } from 'src/components/organisms/data-grid';

type ColumnFieldId =
  | 'empty-space'
  | 'index'
  | 'name'
  | 'category'
  | 'previewImageFilePath'
  | 'createdAt'
  | 'status'
  | 'likes'
  | 'price'
  | 'reviews'
  | 'author'
  | 'participantsQuantity'
  | 'firstName'
  | 'lastName'
  | 'displayName'
  | 'email'
  | 'reason'
  | 'reporter'
  | 'reportedAt'
  | 'amountOfReports';

interface LocalGridColDef extends GridColDef {
  field: ColumnFieldId;
}

export const useGridColumns = () => {
  const { t } = useTranslation();

  const columns: LocalGridColDef[] = [
    { field: 'empty-space', headerName: '', flex: 1, sortable: false },
    { field: 'index', type: 'number', headerName: '', width: 60, sortable: false },
    {
      field: 'name',
      headerName: t('defaults.grid.columns.name'),
      width: 400,
      renderCell: params => (
        <LinkCellWrapper to={mymeDetailsPageRoute(params.row.id)}>
          <TooltipCell value={params.value} width={380} />
        </LinkCellWrapper>
      )
    },
    {
      field: 'category',
      headerName: t('defaults.grid.columns.category'),
      width: 250
    },
    {
      field: 'previewImageFilePath',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: params => <ImageCell src={params.value} defaultPhoto='/Logo.png' />
    },
    {
      field: 'status',
      headerName: t('defaults.grid.columns.status'),
      width: 120
    },
    {
      field: 'createdAt',
      headerName: t('defaults.grid.columns.created_at'),
      type: 'number',
      width: 140
    },
    {
      field: 'participantsQuantity',
      headerName: t('defaults.grid.columns.participants_quantity'),
      type: 'number',
      width: 110
    },
    {
      field: 'price',
      headerName: t('defaults.grid.columns.price'),
      type: 'number',
      width: 80,
      renderCell: params => <PriceCell price={params.value} />
    },
    {
      field: 'likes',
      headerName: t('defaults.grid.columns.likes'),
      type: 'number',
      width: 80
    },
    {
      field: 'reviews',
      headerName: t('defaults.grid.columns.reviews'),
      type: 'number',
      width: 80
    },
    {
      field: 'author',
      headerName: t('defaults.grid.columns.author'),
      renderCell: ({ value }) => <LinkCellWrapper to={creatorProfilePageRoute(value.id)}>{value.name}</LinkCellWrapper>,
      width: 300
    },
    {
      field: 'firstName',
      headerName: t('defaults.grid.columns.first_name'),
      width: 220
    },
    {
      field: 'lastName',
      headerName: t('defaults.grid.columns.last_name'),
      width: 220
    },
    {
      field: 'displayName',
      headerName: t('defaults.grid.columns.display_name'),
      width: 275
    },
    {
      field: 'email',
      headerName: t('defaults.grid.columns.email'),
      width: 300
    },
    {
      field: 'reason',
      headerName: t('defaults.grid.columns.reason'),
      width: 400
    },
    {
      field: 'reporter',
      headerName: t('defaults.grid.columns.reporter'),
      width: 400,
      renderCell: params => (
        <LinkCellWrapper to={creatorProfilePageRoute(params.value.id)}>{params.value.name}</LinkCellWrapper>
      )
    },
    {
      field: 'reportedAt',
      headerName: t('defaults.grid.columns.reported_at'),
      type: 'number',
      width: 140
    },
    {
      field: 'amountOfReports',
      headerName: t('defaults.grid.columns.amount_of_reports'),
      type: 'number',
      width: 140
    }
  ];

  const getColumn = (fieldId: ColumnFieldId) => columns.find(col => col.field === fieldId) as LocalGridColDef;

  const getColumns = (fieldsIds: ColumnFieldId[]) => fieldsIds.map(fieldId => getColumn(fieldId));

  return { getColumn, getColumns };
};
